import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Upload, Search, Download, Trash2, FileText, Image as ImageIcon, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CATS = ["Property", "Tenant", "Lease", "Insurance", "Taxes", "Maintenance"];

export default function Documents() {
  const { canWrite } = useAuth();
  const [sp, setSp] = useSearchParams();
  const propertyId = sp.get("property_id") || "";
  const tenantId = sp.get("tenant_id") || "";
  const recordName = sp.get("property_name") || sp.get("tenant_name") || "";
  const [docs, setDocs] = useState([]);
  const [props, setProps] = useState([]);
  const [leases, setLeases] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const fileRef = useRef();
  const [upload, setUpload] = useState({ category: "Property", property_id: "", property_name: "" });

  const load = useCallback(async () => {
    const [d, p, l] = await Promise.all([api.get("/documents"), api.get("/properties"), api.get("/leases")]);
    setDocs(d.data); setProps(p.data); setLeases(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearRecordFilter = () => { sp.delete("property_id"); sp.delete("tenant_id"); sp.delete("property_name"); sp.delete("tenant_name"); setSp(sp, { replace: true }); };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/documents", {
          name: file.name, category: upload.category,
          property_id: upload.property_id, property_name: upload.property_name,
          file_type: file.name.split(".").pop().toLowerCase(),
          size_kb: Math.round(file.size / 1024), data: reader.result,
        });
        toast.success("Document uploaded");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    };
    reader.readAsDataURL(file);
  };

  const download = (d) => {
    if (!d.data) { toast.info("This is a demo document (no file attached)"); return; }
    const a = document.createElement("a");
    a.href = d.data; a.download = d.name; a.click();
  };

  const remove = async (d) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/documents/${d.id}`);
    toast.success("Deleted"); load();
  };

  const tenantLeaseIds = new Set(leases.filter((l) => l.tenant_id === tenantId).map((l) => l.id));
  const matchesRecord = (d) => {
    if (propertyId) return d.property_id === propertyId;
    if (tenantId) return d.tenant_id === tenantId || (d.linked_collection === "leases" && tenantLeaseIds.has(d.linked_id));
    return true;
  };
  const filtered = docs.filter((d) =>
    matchesRecord(d) &&
    (filterCat === "All" || d.category === filterCat) &&
    (!search || JSON.stringify(d).toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="page-documents">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Document Repository</h1>
        <p className="text-sm text-muted-foreground mt-1">{filtered.length} documents · organized by category</p>
      </div>

      {(propertyId || tenantId) && (
        <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm" data-testid="doc-record-filter">
          <span className="text-muted-foreground">Showing documents for</span>
          <span className="font-semibold">{recordName || (propertyId ? "property" : "tenant")}</span>
          <button onClick={clearRecordFilter} data-testid="doc-clear-filter" className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /> Clear</button>
        </div>
      )}

      {canWrite && (
        <div className="border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
            <Select value={upload.category} onValueChange={(v) => setUpload({ ...upload, category: v })}>
              <SelectTrigger className="rounded-xl w-40" data-testid="upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Property</label>
            <Select value={upload.property_name} onValueChange={(v) => { const p = props.find((x) => x.name === v); setUpload({ ...upload, property_name: v, property_id: p?.id || "" }); }}>
              <SelectTrigger className="rounded-xl w-52" data-testid="upload-property"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="file-input" />
          <Button className="rounded-xl" onClick={() => fileRef.current.click()} data-testid="upload-button">
            <Upload className="w-4 h-4 mr-1" /> Upload Document
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search documents..." className="pl-9 w-56 rounded-xl" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="doc-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="rounded-xl w-40" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="All">All Categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => {
          const isImg = ["png", "jpg", "jpeg"].includes(d.file_type);
          return (
            <div key={d.id} className="border border-border bg-card p-4 flex items-start gap-3" data-testid={`doc-${d.id}`}>
              <div className="w-10 h-10 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                {isImg ? <ImageIcon className="w-5 h-5" strokeWidth={1.5} /> : <FileText className="w-5 h-5" strokeWidth={1.5} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.name}</p>
                <p className="text-xs text-muted-foreground">{d.category} · {d.property_name || "—"} · {d.size_kb}KB</p>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => download(d)} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary" data-testid={`download-${d.id}`}><Download className="w-3 h-3" /> Download</button>
                  {canWrite && <button onClick={() => remove(d)} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /> Delete</button>}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No documents found.</p>}
      </div>
    </div>
  );
}
